/**
 * @file ErrorAlert.tsx
 *
 * @description This component is designed for use in a law enforcement application to present
 * error states to police officers with a clear way to retry failed operations.
 * It follows Material UI design principles for consistency with the rest of the application.
 *
 * @module Components
 * @requires Material-UI
 * @requires React
 */

import React from "react";
import { Alert, Button, Box } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";

/**
 * Props for the ErrorAlert component
 * @interface ErrorAlertProps
 * @property {string} message - The error message to display to the user.
 * @property {() => void} onRetry - Callback function that will be executed when the retry button is clicked.
 */
interface ErrorAlertProps {
  message: string;
  onRetry: () => void;
}

/**
 * @name ErrorAlert
 *
 * @description A component that displays an error message with a retry button.
 *
 * @example
 * <ErrorAlert
 *   message="Failed to load incident data. Please try again."
 *   onRetry={() => fetchIncidentData()}
 * />
 *
 * @param {ErrorAlertProps} props - The component props
 * @param {string} props.message - The error message to display
 * @param {() => void} props.onRetry - Function to call when the retry button is clicked
 *
 * @returns {React.ReactElement} A styled error alert with retry functionality
 */
const ErrorAlert: React.FC<ErrorAlertProps> = ({ message, onRetry }) => {
  return (
    // Container box with bottom margin to separate from other content
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="error"
        action={
          // Refresh button that will trigger the onRetry callback
          <Button
            color="inherit" // Inherit color to match the Alert styling
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            Refresh
          </Button>
        }
      >
        {/* Display the error message passed as a prop */}
        {message}
      </Alert>
    </Box>
  );
};

export default ErrorAlert;
